import { cn } from '@/shared/lib/utils/tw';

import type { ReactNode } from 'react';

/**
 * 페이지 최상위 컨테이너 — TRAD-2109 (UI 일관성 룰 4: 공통 스캐폴드).
 *
 * ## 왜 만드는가
 * 2026-08-09 실측: `src/pages` 의 본 렌더 컨테이너가 **25종**으로 갈려 있었다. 새 화면을 만들 때
 * 기준이 없어 매번 새로 적다 보니, 폭·여백이 화면마다 미세하게 어긋난다(웹 UI 감사 2099 의
 * 근본 원인). 개별 화면을 고치는 것으로는 재발을 못 막는다 — **조합만으로 페이지를 짜게** 만들어
 * 어긋날 여지 자체를 없애는 게 이 컴포넌트의 목적이다.
 *
 * ## 축은 3개뿐이다 (실측에서 나온 것만 넣는다)
 * 25종을 늘어놓고 보면 실제로 갈리는 축은 **폭 / 패딩 / 세로 간격** 뿐이고, `mx-auto` 는 42개
 * 전부가 공통이었다. 그래서 `mx-auto` 는 고정하고 나머지 셋만 prop 으로 뽑았다.
 * 실측에 없던 값은 **일부러 넣지 않았다** — 쓰이지 않는 선택지는 다음 사람이 고민할 거리만 늘린다.
 *
 * ## ~~🔴 이 컴포넌트는 화면을 바꾸지 않는다~~ → **③-b 부터는 바꾼다** (2026-09-07)
 * ~~리팩터링이므로 이관 전후 렌더 결과가 같아야 한다.~~
 * 🔴 **원문을 지우지 않고 정정만 얹는다.** ①이관(③-a까지)은 렌더 무변경이 맞았고 그 전제로
 * 가드를 짰다. ②**③-b 는 축의 *값*을 바꾸는 작업이라 렌더가 의도적으로 바뀐다**(davidyoon
 * "PC 좌우 패딩이 뒤죽박죽" → designer 스펙 §3 안 2). 두 문장이 모순이 아니라 **단계가 다르다.**
 *   `__tests__/PageShell.test.tsx`              축의 **값**이 설계대로인가 (손으로 적은 조합표)
 *   `shared/lib/__tests__/page-shell-migration-parity.test.ts`  각 화면의 **축 배정**이 설계대로인가
 */

/**
 * 폭 — designer 스펙 §5-3 (2026-09-07 ③-b 에서 값 확정)
 *
 * ## ~~`full` 축~~ — **삭제됐다 (③-b, 2026-09-07)**
 * ~~미이관 4화면이 폭 제한을 아예 안 걸어서, 이관을 가능하게 하려고 만든 과도기 축.~~
 * 🔴 **처분이 예고돼 있었고 그대로 집행했다** — designer 스펙 §5-3, 그리고 ③-a 커밋의 이 주석에
 * *"4단계(축 재정의)에서 흡수하고 이 축을 지운다 · 지우는 시점 = 4단계와 같은 커밋"* 이라고
 * 적어둔 것의 집행이다. **적어둔 대로 집행된 사례로 남긴다.**
 *
 * 7곳의 처분(전부 designer 배정):
 *   `/market` · `/interest` · `MarketDetail`            → `data`  (유동 + 거터. 🔴 좁아진다 — 알려진 대가)
 *     🔴 **2026-09-14(TRAD-3417) — 이 「알려진 대가」라는 라벨이 이 건을 통과시켰다.** 원문은
 *     지우지 않고 수치를 얹는다. **얼마나·어느 폭에서**가 빠져 있어서 「데스크톱에서 좀
 *     좁아진다」로 읽혔다. 실제 비율:
 *         데스크톱 2560px  거터 40px = **1.6%**
 *         모바일   375px   거터 40px = **21%** (좌우 합 80px)
 *     그 결과 2026-09-07 ~ 09-14 동안 `/market` 시세 그리드의 가로 스크롤 창이
 *     **≈207px → 135px** 로 줄었고, 24h Amount 컬럼(minWidth 156px)이 창보다 넓어져
 *     **끝까지 밀면 값의 맨 앞자리가 잘렸다**(`65,010,565,376` → 화면엔 `5,010,565,376`,
 *     「…」 없음). davidyoon 이 제보해서 알았다.
 *     🔴 **라벨은 위험을 「알리는」 게 아니라 「면제」해 줄 수 있다.** 다음 사람이 이 줄을
 *     다시 면제로 쓰지 않도록 수치를 박아 둔다 — 아래 `GUTTER_CLASS` 에 모바일 분기를 넣어
 *     해소했지만, **라벨을 지우지는 않는다.**
 *   `/my-page/notifications`                            → `app`   (🔴 1426 → 1280 으로 **좁아진다**)
 *   `VoteHistory` · `Setting` · `NotificationSettings`  → `read`   (576/576/640 → 768)
 *
 * 🔴 **그 3곳은 `width="full"` 이면서 `className` 으로 `max-w-*` 를 되걸고 있었다**
 * (`max-w-screen-sm` · `max-w-xl` · `max-w-xl`). 아래에서 금지한 *"`className="max-w-none"` 으로
 * 축을 무효화하는 편법"* 의 **정확한 거울상**이다 — 소스만 읽는 사람에게 "이 화면은 폭 제한이
 * 없다"고 거짓말한다. ③-b 에서 그 `max-w-*` 를 전부 걷어냈다.
 * (designer 스펙 §5-3 이 같은 3곳을 *"576/576/640 — davidyoon 이 말한 「제각각」의 정확한 표본"*
 *  으로 독립적으로 지목했다. **두 사람이 따로 같은 표본에 도달했다.**)
 *
 * 🔴 **`className="max-w-none"` 으로 축을 덮는 편법은 여전히 금지다.** tailwind-merge 가 실제로
 * 지워주긴 하지만, 그러면 **축을 쓰면서 축을 무효화하는** 상태가 되어 소스만 읽는 다음 사람에게
 * 거짓말한다. 축이 없으면 축을 만드는 게 맞다.
 */
export type PageShellWidth = 'data' | 'app' | 'read' | 'form';

/**
 * 패딩 — 실측 상위: `p-4` 17, `px-4 py-6` 17, `p-5 lg:p-8` 3(정책 문서류).
 *
 * ## ~~`spacious`(`px-4 py-10`)~~ — **`comfortable` 에 흡수됐다 (③-b)**
 * ~~배치 5 에서 예측 상세를 이관하려다 발견해 추가했다.~~ designer 스펙 §5-3: 세로 여백이
 * 화면마다 갈리는 것도 "제각각"의 일부다. 유일한 사용처였던 `PolymarketDetailPage`(3곳)를
 * `comfortable` 로 보냈다 — **py-10 → py-6 으로 세로 여백이 16px 줄어든다.**
 */
export type PageShellPadding = 'default' | 'comfortable' | 'document' | 'none';

/** 세로 간격 — 실측: space-y-4 10, space-y-6 1(홈) */
export type PageShellGap = 'none' | 'sm' | 'lg';

const WIDTH_CLASS: Record<PageShellWidth, string> = {
  /**
   * 시세·표·카드 그리드. **유동 + 거터** (designer 스펙 §3 안 2, davidyoon 승인).
   *   content = min(vw − 2×gutter, 2360)   gutter = 40(vw<1536) / 100(vw≥1536)
   *
   * 🔴 **여기 적힌 2560 과 스펙의 2360 은 서로 다른 박스를 재는 값이다.** Tailwind 는
   * `box-sizing: border-box` 라 `max-width` 가 **패딩을 포함**한다 → 2560 − 200 = 2360(내용폭).
   * `max-w-[2360px]` 로 걸면 내용폭이 2160 이 되어 **스펙보다 200 좁아진다.** (designer 확인)
   *
   * 🔴 **거터는 여기 두지 않는다 — `GUTTER_CLASS` 로 뺐다.** 이유는 그쪽 주석에.
   */
  data: 'w-full max-w-[2560px]',
  /**
   * 일반 목록·혼합 화면. **1024 → 1280** (③-b).
   * ③-a 에서 종전 `medium`(screen-lg)·`list`(5xl)를 합쳐 만든 축이고(둘 다 1024 로 실측 확인),
   * 이번에 값을 1280 으로 올린다. `/derivatives`·`/economic-indicators` 등이 +256 넓어진다.
   */
  app: 'max-w-screen-xl',
  /** 글·설정·정책. 🔴 어느 안에서도 값을 안 건드린다(designer 스펙 §3). */
  read: 'max-w-3xl',
  form: 'max-w-2xl',
};

/**
 * 🔴 거터 — `data` 축의 좌우 여백. **`WIDTH_CLASS` 가 아니라 별도 슬롯인 이유가 있다.**
 *
 * 거터는 가로 **패딩**이라 `PADDING_CLASS` 와 같은 CSS 속성을 다툰다. `cn()` 인자 순서상
 * 폭이 패딩보다 앞이므로, 거터를 `WIDTH_CLASS.data` 에 넣으면 **뒤에 오는 패딩이 거터를 덮는다.**
 * 추정이 아니라 설치본 tailwind-merge 로 실측했다(2026-09-07):
 *
 *   'px-10' 뒤에 'p-4'            → "p-4"                🔴 거터가 통째로 사라진다
 *   'p-4' 뒤에 'px-10'            → "p-4 px-10"          🔴 둘 다 남아 CSS 출력순이 결정 = 판정 불가
 *   'px-4 py-4' 뒤에 'px-10'      → "py-4 px-10"         ✅ 명확
 *
 * 그래서 **①거터를 패딩 뒤 별도 슬롯에 두고 ②`default` 를 `p-4`→`px-4 py-4` 로 바꿔** 모호성을
 * 없앴다. ②는 **CSS 가 완전히 동일하다**(`p-4` ≡ `px-4 py-4`) — 지어낸 게 아니라 parity 테스트
 * 정규화 ①이 근거와 함께 이미 고정해 둔 사실이다. **렌더는 그대로고 판정 가능성만 얻는다.**
 *
 * 🔴 **`className` 은 거터보다 뒤에 온다 — 화면이 `className` 에 `p-*`/`px-*` 를 넘기면 거터가
 * 죽는다.** ③-b 착수 시 전수 감사해서 실제 충돌 2곳(`market.tsx`·`Interest.tsx` 의 `p-1`)을
 * 걷어냈다. **`data` 축 화면에 `className` 으로 가로 패딩을 주지 말 것** — 죽는 쪽은 거터다.
 *
 * 🔴 **`px-25` 는 존재하지 않는다.** Tailwind v3 기본 spacing 사다리는 …20·24·28… 이라 25 가
 * 없고(설치본 3.4.17 `stubs/config.full.js` 확인), 이 레포는 `theme.extend.spacing` 도 없다.
 * Tailwind 는 모르는 유틸을 **에러 없이 그냥 안 만들므로** `px-25` 를 쓰면 조용히 패딩 0 이 된다.
 * `2xl` 브레이크포인트는 기본값 1536px 이라 스펙의 거터 전환점과 정확히 일치한다.
 */
const GUTTER_CLASS: Record<PageShellWidth, string> = {
  /**
   * 🔴 TRAD-3417 (2026-09-14) — `'px-10 2xl:px-[100px]'` → `'px-4 md:px-10 2xl:px-[100px]'`.
   *
   * ## 이건 디자인 결정을 뒤집는 게 아니라 «빠진 분기»를 채우는 것이다
   * designer 스펙 §3 안 2 가 정한 것은 **「거터를 준다」와 「1536 위아래로 40/100」** 이다.
   * **모바일에서 얼마인지는 정해진 적이 없다** — 브레이크포인트가 `2xl` 하나뿐이라
   * **데스크톱 값(40px)이 폰까지 폴백으로 흘러내렸다.** 375px 폰에서 좌우 80px = 화면의 21%.
   * `md`(768) 이상은 여전히 `px-10` 이라 **데스크톱은 1픽셀도 안 바뀐다**(테스트로 고정).
   *
   * ## 왜 지금 고치나 — 증상은 시세 표에서 났지만 원인은 여기였다
   * TRAD-3266 이 「근본: 136px 안에 330px 를 넣는 것 자체」라고 **정확히 지목해 놓고**, 그
   * 136px 을 **이틀 전 커밋(`1a60a64c`, 2026-09-07)** 이 만들었다는 걸 못 봤다. 조사가 그리드
   * **안쪽**(AG Grid 소스)만 보고 그리드를 **담은 상자**를 안 봤다.
   * 🔴 **증상이 보이는 층과 원인이 있는 층이 다르다**의 사례로 남긴다.
   *
   * ## 🔴 `px-4` 를 모바일 값으로 고른 이유와, 그게 깨뜨리는 가드
   * 375px 기준 내용폭 343px → 시세 그리드 스크롤 창 **183px** ≥ 24h Amount 156px. 실측으로
   * 확인했다(`scripts/verify-trad3417-mobile-columns.cjs`, 창 183px 에서 좌우 잘림 0px).
   * 🔴 **대가**: `PADDING_CLASS.default` 의 가로값도 `px-4` 라, 「거터가 패딩에 먹혔는지」를
   * `px-4` 의 잔존으로 판정하던 종전 가드가 **이 두 상태를 구분할 수 없게 된다**(실측: 두 순서가
   * 같은 클래스 집합을 낸다). 그래서 그 가드의 판정 기준을 `md:px-10` 의 잔존 + 가로값이 다른
   * `document` 패딩(`p-5`)을 탐침으로 쓰는 쪽으로 바꿨다 — `__tests__/PageShell.test.tsx` 참고.
   */
  data: 'px-4 md:px-10 2xl:px-[100px]',
  app: '',
  read: '',
  form: '',
};

const PADDING_CLASS: Record<PageShellPadding, string> = {
  /** 🔴 `p-4` 와 CSS 동일. 거터와의 모호성을 없애려고 분해했다 — 위 GUTTER_CLASS 주석 참고. */
  default: 'px-4 py-4',
  comfortable: 'px-4 py-6',
  document: 'p-5 lg:p-8',
  none: '',
};

const GAP_CLASS: Record<PageShellGap, string> = {
  none: '',
  sm: 'space-y-4',
  lg: 'space-y-6',
};

export interface PageShellProps {
  children: ReactNode;
  width?: PageShellWidth;
  padding?: PageShellPadding;
  gap?: PageShellGap;
  /**
   * 이관 대상 화면이 들고 있던 **그 화면 고유의 클래스**를 그대로 넘기는 자리
   * (`text-left`·`relative`·`pb-12` 등). 여기 없는 것을 새로 붙이지 말 것 —
   * 공통 축이 되어야 할 값이면 위 3축에 추가하는 게 맞다.
   *
   * 🔴 **여기에 `max-w-*` 를 넣지 말 것**(폭 축을 무효화한다) · 🔴 **`data` 축에서는 `p-*`/`px-*`
   * 도 넣지 말 것**(거터를 죽인다). 둘 다 ③-b 에서 실제로 걷어낸 사례가 있다.
   */
  className?: string;
}

/**
 * 순수 함수 — 클래스 조합만 담당한다. 렌더 없이도 테스트할 수 있어서
 * "조합표가 설계대로인가"를 DOM 없이 고정할 수 있다.
 *
 * 🔴 인자 순서가 곧 우선순위다: 뒤가 앞을 덮는다. **거터가 패딩 뒤**에 있는 것이 핵심이다.
 */
export const pageShellClassName = ({
  width = 'data',
  padding = 'default',
  gap = 'none',
  className,
}: Omit<PageShellProps, 'children'> = {}): string =>
  cn('mx-auto', WIDTH_CLASS[width], PADDING_CLASS[padding], GUTTER_CLASS[width], GAP_CLASS[gap], className);

export const PageShell = ({
  children,
  width = 'data',
  padding = 'default',
  gap = 'none',
  className,
}: PageShellProps) => <div className={pageShellClassName({ width, padding, gap, className })}>{children}</div>;
